import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokensSet, setTokensSet] = useState(false);

  useEffect(() => {
    const setTokensFromUrl = async () => {
      // Versuche zuerst Query-Parameter
      let accessToken = searchParams.get('access_token');
      let refreshToken = searchParams.get('refresh_token');
      
      // Falls nicht gefunden, versuche Hash-Parameter
      if (!accessToken || !refreshToken) {
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        accessToken = hashParams.get('access_token');
        refreshToken = hashParams.get('refresh_token');
      }

      console.log('Tokens gefunden:', { accessToken: !!accessToken, refreshToken: !!refreshToken });

      if (accessToken && refreshToken) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            console.error('Fehler beim Setzen der Session:', error);
            setError('Fehler beim Verarbeiten des Reset-Links');
          } else {
            console.log('Session erfolgreich gesetzt:', data);
            setTokensSet(true);
          }
        } catch (err) {
          console.error('Unerwarteter Fehler beim Setzen der Session:', err);
          setError('Unerwarteter Fehler beim Verarbeiten des Reset-Links');
        }
      }
    };

    setTokensFromUrl();
  }, [searchParams]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      console.log('Passwort erfolgreich aktualisiert');
      setSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
      
      // Nach 3 Sekunden zur Anmeldung weiterleiten
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error: any) {
      console.error('Fehler beim Zurücksetzen des Passworts:', error);
      setError('Fehler beim Zurücksetzen des Passworts. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Zeige Loading-State während Token-Verarbeitung
  if (!tokensSet && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
            <CardTitle className="text-xl text-emerald-900">Link wird verarbeitet...</CardTitle>
            <CardDescription>
              Bitte warte, während wir deinen Reset-Link verarbeiten.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Zeige Fehler-State wenn Token nicht gesetzt werden konnten
  if (!tokensSet && error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-red-600">Ungültiger Link</CardTitle>
            <CardDescription>
              Der Reset-Link ist ungültig oder abgelaufen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 p-3 rounded text-red-600 text-sm mb-4">
              {error}
            </div>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl text-emerald-900">Passwort zurückgesetzt!</CardTitle>
            <CardDescription>
              Dein Passwort wurde erfolgreich zurückgesetzt. Du wirst automatisch zur Anmeldung weitergeleitet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Jetzt anmelden
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-emerald-600" />
          </div>
          <CardTitle className="text-xl text-emerald-900">Neues Passwort festlegen</CardTitle>
          <CardDescription>
            Gib dein neues Passwort ein
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                required
                minLength={6}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                required
                minLength={6}
                className="w-full"
              />
            </div>
            {error && (
              <div className="bg-red-50 p-3 rounded text-red-600 text-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Wird gespeichert...
                </>
              ) : (
                'Passwort zurücksetzen'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 